import { Badge } from '@/components/ui/badge';
import type { ComplianceLineItem } from '@/services';

export const checkLabels: Record<string, string> = {
  price_match: 'Price Match',
  quantity_match: 'Quantity Match',
  item_code_valid: 'Item Code Valid',
  uom_consistent: 'UOM Consistent',
  discount_applied: 'Discount Applied',
  line_total_accurate: 'Line Total Accurate',
  contract_item: 'Contract Item',
  ticket_item: 'Ticket Item',
};

export function formatComplianceCurrency(val: number | null | undefined): string {
  if (val == null) return '—';
  return `$${val.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
}

export function DiscrepancyDetail({ item }: { item: ComplianceLineItem }) {
  return (
    <div>
      <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">Discrepancy</div>
      <div className="border border-border rounded p-3 space-y-2">
        {item.types && (
          <Badge variant="outline" className="text-[10px]">{item.types}</Badge>
        )}
        <div className="grid grid-cols-5 gap-3 text-xs">
          <div>
            <div className="text-muted-foreground font-medium">Invoiced</div>
            <div className="font-mono">{formatComplianceCurrency(item.invoiced_amount)}</div>
          </div>
          <div>
            <div className="text-muted-foreground font-medium">Correct</div>
            <div className="font-mono">{formatComplianceCurrency(item.correct_amount)}</div>
          </div>
          <div>
            <div className="text-muted-foreground font-medium">Diff/Unit</div>
            <div className="font-mono">{formatComplianceCurrency(item.difference_per_unit)}</div>
          </div>
          <div>
            <div className="text-muted-foreground font-medium">Qty</div>
            <div className="font-mono">{item.quantity ?? '—'}</div>
          </div>
          <div>
            <div className="text-muted-foreground font-medium">Impact</div>
            <div className="font-mono font-medium text-red-600 dark:text-red-400">{formatComplianceCurrency(item.total_impact)}</div>
          </div>
        </div>
      </div>
    </div>
  );
}

export function ComplianceLineItemDetail({ item }: { item: ComplianceLineItem }) {
  return (
    <div className="space-y-3">
      {/* Notes */}
      {item.notes && (
        <div>
          <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">Notes</div>
          <p className="text-sm text-foreground">{item.notes}</p>
        </div>
      )}
      {/* Checks */}
      <div>
        <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-1">Checks</div>
        <div className="flex flex-wrap gap-2">
          {Object.entries(item.checks).map(([key, rawValue]) => {
            const label = checkLabels[key] || key;
            let value: string;
            if (rawValue == null) value = 'N/A';
            else if (typeof rawValue === 'boolean') value = rawValue ? 'Yes' : 'No';
            else if (typeof rawValue === 'object') return null;
            else value = String(rawValue);

            let badgeClass = 'border-muted text-muted-foreground';
            if (value === 'PASS') badgeClass = 'border-green-300 text-green-700 dark:border-green-700 dark:text-green-400';
            else if (value === 'FAIL') badgeClass = 'border-red-300 text-red-700 dark:border-red-700 dark:text-red-400';
            else if (value === 'FLAG') badgeClass = 'border-yellow-300 text-yellow-700 dark:border-yellow-700 dark:text-yellow-400';
            return (
              <Badge key={key} variant="outline" className={badgeClass}>
                {label}: {value}
              </Badge>
            );
          })}
        </div>
      </div>

      {/* Discrepancy */}
      {item.types != null && (
        <DiscrepancyDetail item={item} />
      )}
    </div>
  );
}
